// expose-services.js — Brief 156.
//
// Exposes a minimal `window.services` surface on dev / test deploys so Brief
// 155's seed snippets (tools/seed/<state>.js) can reach the real learner-store
// funnel (`markItemCompleted`) from the DevTools Console at
// test.kungfuenglish.com. On `npm run dev` the snippet falls back to importing
// raw `/src/**` modules; the esbuild-bundled deploy does not serve `/src/**`,
// so without this hook the snippet 404s its dynamic imports and makes no writes.
//
// SECURITY INVARIANT — production NEVER exposes services. The gate is an
// EXPLICIT HOSTNAME ALLOWLIST (not a build flag). The same bundle deployed to
// test.kungfuenglish.com exposes services; deployed to the production apex
// (kungfuenglish.com), a `www.` alias, or any CDN host (e.g.
// video.kungfuenglish.com) it does not — those hostnames are simply not in the
// allowlist. Hostname-gating beats build-flag gating here because it needs no
// separate dev/test build artifact: one bundle, behaviour decided at runtime by
// where it is served.
//
// Only `learner` is exposed — the single surface Brief 155's snippets touch.
// A blanket `window.services = services` would hand a DevTools-savvy learner a
// larger blast radius (audio, pathway, …); keep it minimal and grow it one
// surface at a time as concrete use cases arise.
//
// If anyone ever proposes adding a production hostname to the allowlist, they
// MUST justify it in their PR and add a unit test that explicitly fails on the
// production hostname being allowed.

/**
 * Hostnames permitted to expose `window.services`. Production hostnames
 * (`kungfuenglish.com` + `www.`/CDN aliases) are absent by design — this is an
 * allowlist, so anything not listed (and not `*.local`) is denied.
 */
export const SERVICES_EXPOSE_HOSTS = new Set([
  "test.kungfuenglish.com",
  "localhost",
  "127.0.0.1",
]);

/**
 * Expose `services.learner` as `window.services.learner` when the current
 * hostname is allowlisted (or a `*.local` Bonjour / mDNS dev host). Pure and
 * dependency-injected so the gate is unit-testable without a real DOM — same
 * shape as `registerServiceWorker` (Brief 111).
 *
 * @param {object} deps
 * @param {{ learner: unknown }} deps.services  the live platform services
 * @param {{ hostname: string }} deps.location  the page location (inject for tests)
 * @param {Record<string, any>} [deps.win]      the global object to assign onto
 * @returns {boolean} whether services were exposed
 */
export function exposeServicesIfAllowed({ services, location, win }) {
  const host = (location && location.hostname) || "";
  const allowed = SERVICES_EXPOSE_HOSTS.has(host) || host.endsWith(".local");
  if (allowed && win) {
    win.services = { learner: services.learner };
  }
  return allowed;
}
